// src/components/Materialoteca/MaterialotecaDrawer.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  Stack,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import {
  getGrupos,
  getSubgrupos,
  addMaterial,
  updateMaterial,
  getProveedores,
  uploadMaterialFotos,
  deleteMaterialFotoById,
  materialFotoPublicUrl,
} from "../../api/Materialoteca/MaterialotecaApi";
import MaterialotecaPhotoLightbox from "./MaterialotecaPhotoLightbox";

const ACCENT = "#ffac7e";
const ACCENT_HOVER = "#e89566";

const fieldSx = (theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.black, 0.22)
        : alpha(theme.palette.background.paper, 0.92),
    transition: "box-shadow 0.16s ease, border-color 0.16s ease",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.divider, 0.85),
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(ACCENT, 0.7),
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    boxShadow: `0 0 0 4px ${alpha(ACCENT, 0.2)}`,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ACCENT,
  },
});

const sortProveedoresByName = (list) =>
  [...(list || [])].sort((a, b) =>
    (a.N_PROVEEDOR || "").localeCompare(b.N_PROVEEDOR || "", undefined, {
      sensitivity: "base",
      numeric: true,
    }),
  );

/** Proveedores catalogados como fabrica (tipo Fabric Mill en maestro de proveedores) */
const isFabricMillProveedor = (p) =>
  (p.tipoProveedor?.ProveedorTipo || "").trim().toLowerCase() === "fabric mill";

const millNameInList = (millName, mills) => {
  const n = (millName || "").trim();
  if (!n) return false;
  return mills.some((p) => (p.N_PROVEEDOR || "").trim() === n);
};

const MaterialotecaDrawer = ({ open, onClose, onSave, material }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [grupos, setGrupos] = useState([]);
  const [subgrupos, setSubgrupos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [pendingPhotoItems, setPendingPhotoItems] = useState([]);
  const [pendingRemoveSavedPhotoIds, setPendingRemoveSavedPhotoIds] = useState(
    [],
  );
  const [photoDropActive, setPhotoDropActive] = useState(false);
  const fileInputRef = useRef(null);
  /** input con capture: en móvil abre la cámara (trasera) para disparar y subir */
  const cameraInputRef = useRef(null);
  const [photoDrawerLightboxOpen, setPhotoDrawerLightboxOpen] = useState(false);
  const [photoDrawerLightboxSrc, setPhotoDrawerLightboxSrc] = useState(null);

  const fabricMillProveedores = useMemo(
    () => sortProveedoresByName(proveedores.filter(isFabricMillProveedor)),
    [proveedores],
  );

  const legacyMillOption =
    form.MillName != null &&
    String(form.MillName).trim() !== "" &&
    !millNameInList(form.MillName, fabricMillProveedores)
      ? form.MillName
      : null;

  useEffect(() => {
    if (open) {
      getGrupos().then(setGrupos);
      getProveedores().then((data) =>
        setProveedores(sortProveedoresByName(data)),
      );
    } else {
      setPendingPhotoItems([]);
      setPendingRemoveSavedPhotoIds([]);
      setPhotoDropActive(false);
      setPhotoDrawerLightboxOpen(false);
      setPhotoDrawerLightboxSrc(null);
      setPendingPhotoItems((prev) => {
        prev.forEach((it) => {
          try {
            URL.revokeObjectURL(it.previewUrl);
          } catch (_) {}
        });
        return [];
      });
    }
  }, [open]);

  useEffect(() => {
    if (form.ID_GRUPO) {
      getSubgrupos(form.ID_GRUPO).then(setSubgrupos);
    }
  }, [form.ID_GRUPO]);

  useEffect(() => {
    if (material) {
      setForm({
        ...material,
        Proveedor: material.CODPROV ?? "",
        Fotos: material.Fotos || [],
      });
    } else {
      setForm({
        NombreMaterial: "",
        ID_GRUPO: "",
        TipoMaterial: "",
        Composicion: "",
        Peso: null,
        AnchoTirada: null,
        MinimumOrderQty: null,
        Precio: null,
        MillName: "",
        Proveedor: "",
        Fotos: [],
      });
    }
  }, [material, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const isAcceptableImageFile = (file) =>
    Boolean(
      file && /^image\/(jpeg|jpg|pjpeg|png|gif|webp)$/i.test(file.type || ""),
    );

  const applyPhotoFile = (file) => {
    if (!isAcceptableImageFile(file)) return;
    const previewUrl = URL.createObjectURL(file);
    setPendingPhotoItems((prev) => [
      ...prev,
      { id: `${Date.now()}-${Math.random()}`, file, previewUrl },
    ]);
  };

  const handlePickPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    files.forEach(applyPhotoFile);
  };

  const handlePickPhotoFromCamera = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    applyPhotoFile(file);
  };

  const handlePhotoDragEnter = (e) => {
    e.preventDefault();
    if (e.dataTransfer.types?.includes("Files")) {
      setPhotoDropActive(true);
    }
  };

  const handlePhotoDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setPhotoDropActive(false);
    }
  };

  const handlePhotoDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handlePhotoDrop = (e) => {
    e.preventDefault();
    setPhotoDropActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    files.forEach(applyPhotoFile);
  };

  const handleRemovePhoto = () => {
    if (pendingPhotoItems.length > 0) {
      pendingPhotoItems.forEach((it) => {
        try {
          URL.revokeObjectURL(it.previewUrl);
        } catch (_) {}
      });
      setPendingPhotoItems([]);
      return;
    }

    if (material?.Id && Array.isArray(form.Fotos) && form.Fotos.length > 0) {
      const ids = form.Fotos.map((f) => f.id_MaterialotecaFoto).filter((id) =>
        Number.isFinite(id),
      );
      setPendingRemoveSavedPhotoIds(ids);
      setForm((prev) => ({ ...prev, Fotos: [] }));
    }
  };

  const handleRemovePendingPhoto = (pendingId) => {
    setPendingPhotoItems((prev) => {
      const current = prev.find((p) => p.id === pendingId);
      if (current?.previewUrl) {
        try {
          URL.revokeObjectURL(current.previewUrl);
        } catch (_) {}
      }
      return prev.filter((p) => p.id !== pendingId);
    });
  };

  const handleRemoveSavedPhoto = (fotoId) => {
    if (!Number.isFinite(fotoId)) return;
    setPendingRemoveSavedPhotoIds((prev) =>
      prev.includes(fotoId) ? prev : [...prev, fotoId],
    );
    setForm((prev) => ({
      ...prev,
      Fotos: (prev.Fotos || []).filter(
        (f) => f.id_MaterialotecaFoto !== fotoId,
      ),
    }));
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      Peso: form.Peso ? Number(form.Peso) : null,
      AnchoTirada: form.AnchoTirada ? Number(form.AnchoTirada) : null,
      MinimumOrderQty: form.MinimumOrderQty
        ? Number(form.MinimumOrderQty)
        : null,
      Precio: form.Precio ? Number(form.Precio) : null,
    };
    delete payload.Id;
    delete payload.CODPROV;
    delete payload.NombreProveedor;
    delete payload.PaisProveedor;
    delete payload.NombreGrupo;
    delete payload.NombreSubgrupo;
    delete payload.FechaCreacion;
    delete payload.FotoArchivo;
    delete payload.Fotos;

    let materialId = material?.Id;
    try {
      if (material?.Id) {
        if (pendingRemoveSavedPhotoIds.length > 0) {
          await Promise.all(
            pendingRemoveSavedPhotoIds.map((fotoId) =>
              deleteMaterialFotoById(material.Id, fotoId),
            ),
          );
        }
        await updateMaterial(material.Id, payload);
      } else {
        const created = await addMaterial(payload);
        materialId =
          created?.Id ??
          created?.id ??
          created?.dataValues?.Id ??
          created?.dataValues?.id;
      }

      if (pendingPhotoItems.length > 0 && materialId) {
        await uploadMaterialFotos(
          materialId,
          pendingPhotoItems.map((it) => it.file),
        );
      }

      pendingPhotoItems.forEach((it) => {
        try {
          URL.revokeObjectURL(it.previewUrl);
        } catch (_) {}
      });
      setPendingPhotoItems([]);
      setPendingRemoveSavedPhotoIds([]);
      onSave();
      onClose();
    } catch (err) {
      console.error("❌ Error al guardar material:", err);
    }
  };

  const displayPhotoSrc =
    pendingPhotoItems[0]?.previewUrl ||
    materialFotoPublicUrl(form.Fotos?.[0]?.Archivo || form.FotoArchivo);

  const inputSx = fieldSx(theme);
  const hasSavedPhotos = Array.isArray(form.Fotos) && form.Fotos.length > 0;
  const hasPhotos =
    pendingPhotoItems.length > 0 || hasSavedPhotos || Boolean(form.FotoArchivo);
  const grupoLabel = grupos.find(
    (g) => String(g.ID_GRUPO) === String(form.ID_GRUPO),
  )?.GRUPO;
  const subgrupoLabel = subgrupos.find(
    (s) => String(s.ID_SUBGRUPO) === String(form.TipoMaterial),
  )?.SUBGRUPO;

  const photoLightboxSubtitle = form.NombreMaterial
    ? `: ${form.NombreMaterial}`
    : "";

  const openDrawerPhotoLightbox = (src) => {
    if (!src) return;
    setPhotoDrawerLightboxSrc(src);
    setPhotoDrawerLightboxOpen(true);
  };

  const sectionHead = (Icon, label) => (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.75 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(145deg, ${alpha(ACCENT, 0.5)} 0%, ${alpha(
            ACCENT,
            0.18,
          )} 100%)`,
          color: theme.palette.text.primary,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.92rem",
          letterSpacing: -0.15,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
    </Stack>
  );

  const metaChipSx = {
    height: 22,
    maxWidth: "100%",
    fontWeight: 700,
    fontSize: "0.68rem",
    bgcolor: alpha(theme.palette.background.paper, 0.55),
    border: `1px solid ${alpha(theme.palette.common.white, 0.35)}`,
    "& .MuiChip-label": {
      px: 0.9,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };

  const photoActionSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 999,
    px: 1.4,
    borderColor: alpha(theme.palette.divider, 0.9),
    color: "text.primary",
    bgcolor: alpha(theme.palette.background.paper, 0.72),
  };

  const photoThumbSx = {
    width: 64,
    height: 64,
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
    position: "relative",
    overflow: "hidden",
    bgcolor: theme.palette.background.paper,
    boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.08)}`,
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        BackdropProps={{
          sx: {
            backgroundColor: alpha("#1a1410", 0.42),
            backdropFilter: "blur(6px)",
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 560 },
            maxWidth: "100%",
            boxSizing: "border-box",
            height: "100%",
            borderTopLeftRadius: { xs: 0, sm: 24 },
            borderBottomLeftRadius: { xs: 0, sm: 24 },
            borderLeft: `1px solid ${alpha(ACCENT, 0.22)}`,
            boxShadow: `-18px 0 48px ${alpha(theme.palette.common.black, 0.2)}`,
            display: "flex",
            flexDirection: "column",
            bgcolor: theme.palette.background.paper,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 2.4,
            pb: 2.1,
            position: "relative",
            overflow: "hidden",
            borderBottom: `1px solid ${alpha(ACCENT, 0.18)}`,
            background: `linear-gradient(125deg, ${alpha(ACCENT, 0.38)} 0%, ${alpha(
              ACCENT,
              0.08,
            )} 42%, ${theme.palette.background.paper} 100%)`,
            "&::before": {
              content: '""',
              position: "absolute",
              width: 180,
              height: 180,
              borderRadius: "50%",
              right: -48,
              top: -72,
              background: alpha(ACCENT, 0.22),
              pointerEvents: "none",
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1.5}
            sx={{ position: "relative" }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="flex-start"
              sx={{ minWidth: 0 }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  background: `linear-gradient(145deg, ${alpha(ACCENT, 0.95)} 0%, ${alpha(
                    ACCENT,
                    0.45,
                  )} 100%)`,
                  color: theme.palette.getContrastText(ACCENT),
                  boxShadow: `0 8px 18px ${alpha(ACCENT, 0.35)}`,
                }}
              >
                <LibraryBooksOutlinedIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: 1.4,
                    color: "text.secondary",
                    lineHeight: 1.2,
                  }}
                >
                  {t("MaterialotecaPage.drawerBadge")}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: -0.4,
                    mt: 0.2,
                    lineHeight: 1.2,
                    fontSize: "1.35rem",
                  }}
                >
                  {material
                    ? t("MaterialotecaPage.drawerTitleEdit")
                    : t("MaterialotecaPage.drawerTitleNew")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.55, lineHeight: 1.4 }}
                >
                  {t("MaterialotecaPage.drawerSubtitle")}
                </Typography>
                {(form.NombreMaterial || grupoLabel || subgrupoLabel) && (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mt: 1.1 }}
                  >
                    {form.NombreMaterial ? (
                      <Chip
                        size="small"
                        label={form.NombreMaterial}
                        sx={metaChipSx}
                      />
                    ) : null}
                    {grupoLabel ? (
                      <Chip size="small" label={grupoLabel} sx={metaChipSx} />
                    ) : null}
                    {subgrupoLabel ? (
                      <Chip
                        size="small"
                        label={subgrupoLabel}
                        sx={metaChipSx}
                      />
                    ) : null}
                  </Stack>
                )}
              </Box>
            </Stack>
            <IconButton
              onClick={onClose}
              aria-label={t("MaterialotecaPage.closeAria")}
              sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.82),
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.08)}`,
                "&:hover": {
                  bgcolor: theme.palette.background.paper,
                },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            px: 2.5,
            py: 2.25,
            background: `linear-gradient(180deg, ${alpha(ACCENT, 0.1)} 0%, ${alpha(
              theme.palette.background.default,
              0.65,
            )} 120px)`,
          }}
        >
          <Box
            onDragEnter={handlePhotoDragEnter}
            onDragLeave={handlePhotoDragLeave}
            onDragOver={handlePhotoDragOver}
            onDrop={handlePhotoDrop}
            sx={{
              mb: 1.5,
              borderRadius: 3,
              overflow: "hidden",
              position: "relative",
              minHeight: displayPhotoSrc ? 220 : 168,
              border: photoDropActive
                ? `1.5px dashed ${ACCENT}`
                : `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              bgcolor: photoDropActive
                ? alpha(ACCENT, 0.14)
                : alpha(theme.palette.background.paper, 0.55),
              boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.06)}`,
              transition: "border-color 0.15s, background-color 0.15s",
            }}
          >
            {displayPhotoSrc ? (
              <Box
                role="button"
                tabIndex={0}
                onClick={() => openDrawerPhotoLightbox(displayPhotoSrc)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openDrawerPhotoLightbox(displayPhotoSrc);
                  }
                }}
                aria-label={t("MaterialotecaPage.photoOpenLightboxAria", {
                  subtitle: photoLightboxSubtitle,
                })}
                sx={{
                  cursor: "zoom-in",
                  outline: "none",
                  "&:focus-visible": {
                    outline: `2px solid ${ACCENT}`,
                    outlineOffset: -2,
                  },
                }}
              >
                <Box
                  component="img"
                  src={displayPhotoSrc}
                  alt=""
                  draggable={false}
                  sx={{
                    width: "100%",
                    height: 220,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.38) 100%)",
                    pointerEvents: "none",
                  }}
                />
                <Chip
                  size="small"
                  icon={<ZoomInIcon sx={{ fontSize: 14 }} />}
                  label={t("MaterialotecaPage.sectionPhoto")}
                  sx={{
                    position: "absolute",
                    left: 12,
                    bottom: 12,
                    height: 26,
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.common.white, 0.92),
                    "& .MuiChip-icon": { color: "text.primary", ml: 0.75 },
                  }}
                />
              </Box>
            ) : (
              <Stack
                spacing={1}
                alignItems="center"
                justifyContent="center"
                sx={{ minHeight: 168, px: 3, py: 3 }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `linear-gradient(145deg, ${alpha(ACCENT, 0.45)} 0%, ${alpha(
                      ACCENT,
                      0.12,
                    )} 100%)`,
                    color: theme.palette.text.primary,
                  }}
                >
                  <ImageOutlinedIcon />
                </Box>
                <Typography sx={{ fontWeight: 800, letterSpacing: -0.2 }}>
                  {t("MaterialotecaPage.photoPlaceholder")}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                  sx={{ maxWidth: 300, lineHeight: 1.5 }}
                >
                  {t("MaterialotecaPage.photoDropHint")}
                </Typography>
              </Stack>
            )}
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            hidden
            onChange={handlePickPhoto}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={handlePickPhotoFromCamera}
          />
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 1.25 }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<PhotoLibraryOutlinedIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={photoActionSx}
            >
              {t("MaterialotecaPage.photoChoose")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddAPhotoOutlinedIcon />}
              onClick={() => cameraInputRef.current?.click()}
              sx={photoActionSx}
            >
              {t("MaterialotecaPage.photoTakeCamera")}
            </Button>
            {hasPhotos && (
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={handleRemovePhoto}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 999,
                }}
              >
                {t("MaterialotecaPage.photoRemove")}
              </Button>
            )}
          </Stack>
          {(hasSavedPhotos || pendingPhotoItems.length > 0) && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 800,
                letterSpacing: 0.4,
                display: "block",
                mb: 1,
              }}
            >
              {t("MaterialotecaPage.photoGalleryTitle")}
            </Typography>
          )}
          {hasSavedPhotos && (
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 1 }}
            >
              {form.Fotos.map((foto) => (
                <Box
                  key={`saved-${foto.id_MaterialotecaFoto}-${foto.Archivo}`}
                  sx={photoThumbSx}
                >
                  <Box
                    component="img"
                    src={materialFotoPublicUrl(foto.Archivo)}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      cursor: "zoom-in",
                    }}
                    onClick={() =>
                      openDrawerPhotoLightbox(
                        materialFotoPublicUrl(foto.Archivo),
                      )
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleRemoveSavedPhoto(foto.id_MaterialotecaFoto)
                    }
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: "rgba(0,0,0,0.5)",
                      color: "#fff",
                      p: "2px",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                    }}
                    aria-label={t("MaterialotecaPage.photoRemove")}
                  >
                    <DeleteOutlineIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          )}
          {pendingPhotoItems.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 1 }}
            >
              {pendingPhotoItems.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    ...photoThumbSx,
                    border: `1px dashed ${alpha(ACCENT, 0.8)}`,
                    bgcolor: alpha(ACCENT, 0.1),
                  }}
                >
                  <Box
                    component="img"
                    src={item.previewUrl}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      cursor: "zoom-in",
                    }}
                    onClick={() => openDrawerPhotoLightbox(item.previewUrl)}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemovePendingPhoto(item.id)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: "rgba(0,0,0,0.5)",
                      color: "#fff",
                      p: "2px",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                    }}
                    aria-label={t("MaterialotecaPage.photoRemove")}
                  >
                    <DeleteOutlineIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          )}
          {pendingPhotoItems.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              {t("MaterialotecaPage.photoSelectedCount", {
                count: pendingPhotoItems.length,
              })}
            </Typography>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 2, lineHeight: 1.45 }}
          >
            {t("MaterialotecaPage.photoHint")}
          </Typography>

          <Box
            sx={{
              p: { xs: 1.75, sm: 2.1 },
              borderRadius: 3,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.05)}`,
            }}
          >
            {sectionHead(
              CategoryOutlinedIcon,
              t("MaterialotecaPage.sectionClassification"),
            )}
            <Stack spacing={1.75}>
              <TextField
                size="small"
                fullWidth
                label={t("MaterialotecaPage.labelMaterialName")}
                name="NombreMaterial"
                value={form.NombreMaterial ?? ""}
                onChange={handleChange}
                sx={inputSx}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.75}>
                <TextField
                  size="small"
                  select
                  fullWidth
                  label={t("MaterialotecaPage.labelGroup")}
                  name="ID_GRUPO"
                  value={form.ID_GRUPO ?? ""}
                  onChange={handleChange}
                  sx={inputSx}
                >
                  {grupos.map((g) => (
                    <MenuItem key={g.ID_GRUPO} value={g.ID_GRUPO}>
                      {g.GRUPO}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  select
                  fullWidth
                  label={t("MaterialotecaPage.labelSubgroup")}
                  name="TipoMaterial"
                  value={form.TipoMaterial ?? ""}
                  onChange={handleChange}
                  disabled={!form.ID_GRUPO}
                  sx={inputSx}
                >
                  {subgrupos.map((s) => (
                    <MenuItem key={s.ID_SUBGRUPO} value={s.ID_SUBGRUPO}>
                      {s.SUBGRUPO}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Stack>

            <Divider
              sx={{ my: 2.5, borderColor: alpha(theme.palette.divider, 0.7) }}
            />

            {sectionHead(
              StraightenOutlinedIcon,
              t("MaterialotecaPage.sectionSpecs"),
            )}
            <Stack spacing={1.75}>
              <TextField
                size="small"
                fullWidth
                label={t("MaterialotecaPage.labelComposition")}
                name="Composicion"
                value={form.Composicion ?? ""}
                onChange={handleChange}
                sx={inputSx}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.75}>
                <TextField
                  size="small"
                  fullWidth
                  label={t("MaterialotecaPage.labelWeight")}
                  name="Peso"
                  type="number"
                  value={form.Peso ?? ""}
                  onChange={handleChange}
                  sx={inputSx}
                />
                <TextField
                  size="small"
                  fullWidth
                  label={t("MaterialotecaPage.labelWidth")}
                  name="AnchoTirada"
                  type="number"
                  value={form.AnchoTirada ?? ""}
                  onChange={handleChange}
                  sx={inputSx}
                />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.75}>
                <TextField
                  size="small"
                  fullWidth
                  label={t("MaterialotecaPage.labelMoq")}
                  name="MinimumOrderQty"
                  type="number"
                  value={form.MinimumOrderQty ?? ""}
                  onChange={handleChange}
                  sx={inputSx}
                />
                <TextField
                  size="small"
                  fullWidth
                  label={t("MaterialotecaPage.labelPrice")}
                  name="Precio"
                  type="number"
                  value={form.Precio ?? ""}
                  onChange={handleChange}
                  inputProps={{ step: "0.01" }}
                  sx={inputSx}
                />
              </Stack>
            </Stack>

            <Divider
              sx={{ my: 2.5, borderColor: alpha(theme.palette.divider, 0.7) }}
            />

            {sectionHead(
              LocalShippingOutlinedIcon,
              t("MaterialotecaPage.sectionSupplierMill"),
            )}
            <Stack spacing={1.75}>
              <TextField
                size="small"
                select
                fullWidth
                label={t("MaterialotecaPage.labelSupplier")}
                name="Proveedor"
                value={form.Proveedor ?? ""}
                onChange={handleChange}
                sx={inputSx}
              >
                {proveedores.map((p) => (
                  <MenuItem key={p.CODPROV} value={p.CODPROV}>
                    {p.N_PROVEEDOR} {p.PAIS ? `(${p.PAIS})` : ""}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                fullWidth
                label={t("MaterialotecaPage.labelMill")}
                name="MillName"
                value={form.MillName ?? ""}
                onChange={handleChange}
                sx={inputSx}
              >
                <MenuItem value="">
                  <em>{t("MaterialotecaPage.millEmpty")}</em>
                </MenuItem>
                {legacyMillOption != null && (
                  <MenuItem value={legacyMillOption}>
                    {String(legacyMillOption).trim()}{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      {t("MaterialotecaPage.millLegacyHint")}
                    </Typography>
                  </MenuItem>
                )}
                {fabricMillProveedores.map((p) => (
                  <MenuItem key={p.CODPROV} value={p.N_PROVEEDOR || ""}>
                    {p.N_PROVEEDOR}
                    {p.PAIS ? ` (${p.PAIS})` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            px: 2.5,
            py: 1.85,
            borderTop: `1px solid ${alpha(ACCENT, 0.16)}`,
            background: `linear-gradient(180deg, ${alpha(
              theme.palette.background.paper,
              0.92,
            )} 0%, ${alpha(ACCENT, 0.08)} 100%)`,
            backdropFilter: "blur(10px)",
          }}
        >
          <Stack direction="row" spacing={1.25} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                px: 2,
                fontWeight: 700,
                textTransform: "none",
                borderRadius: 999,
                color: "text.secondary",
                borderColor: alpha(theme.palette.divider, 0.9),
                bgcolor: alpha(theme.palette.background.paper, 0.7),
              }}
            >
              {t("MaterialotecaPage.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                px: 2.75,
                minWidth: 124,
                fontWeight: 800,
                textTransform: "none",
                borderRadius: 999,
                bgcolor: ACCENT,
                color: theme.palette.getContrastText(ACCENT),
                boxShadow: `0 10px 22px ${alpha(ACCENT, 0.38)}`,
                "&:hover": {
                  bgcolor: ACCENT_HOVER,
                  boxShadow: `0 12px 26px ${alpha(ACCENT_HOVER, 0.42)}`,
                },
              }}
            >
              {t("MaterialotecaPage.save")}
            </Button>
          </Stack>
        </Box>
      </Drawer>
      <MaterialotecaPhotoLightbox
        open={photoDrawerLightboxOpen}
        src={photoDrawerLightboxOpen ? photoDrawerLightboxSrc : null}
        alt={form.NombreMaterial || ""}
        onClose={() => {
          setPhotoDrawerLightboxOpen(false);
          setPhotoDrawerLightboxSrc(null);
        }}
        closeAriaLabel={t("MaterialotecaPage.photoLightboxCloseAria")}
      />
    </>
  );
};

export default MaterialotecaDrawer;
